/**
 * Shared series-classification helpers used by both
 * `dicomMetadataLoader.pickPreferredSeries` (which series to auto-load when
 * none is selected) and `hangingProtocolService.assignSeriesToSlots` (which
 * series fills each hanging-protocol slot).
 *
 * Centralized so the product rules (head/brain vs body-region priority, plane
 * derivation from IOP) live in one place instead of being duplicated.
 */

import type { SeriesMetadata } from "./dicomMetadataLoader";

export type Plane = "Axial" | "Coronal" | "Sagittal";

const HEAD_BODY_PART_RE = /BRAIN|HEAD|SKULL/;
const HEAD_DESC_RE = /brain|head|skull/;
const BODY_BODY_PART_RE = /ABDOMEN|KIDNEY|PELVIS|RETROPERITONEUM|URINARY|BODY/;
const BODY_DESC_RE = /abdom|kidney|renal|pelvis|urin|stone|\bbody\b|\bku\b/;

export function isHeadSeries(meta: SeriesMetadata): boolean {
  return (
    HEAD_BODY_PART_RE.test((meta.body_part ?? "").toUpperCase()) ||
    HEAD_DESC_RE.test((meta.series_description ?? "").toLowerCase())
  );
}

export function isBodySeries(meta: SeriesMetadata): boolean {
  return (
    BODY_BODY_PART_RE.test((meta.body_part ?? "").toUpperCase()) ||
    BODY_DESC_RE.test((meta.series_description ?? "").toLowerCase())
  );
}

/**
 * Region priority for slot assignment and auto-load:
 *   0 = body (best — abdomen/kidney/pelvis/...)
 *   1 = neutral (lung/bone/unknown)
 *   2 = head/brain (last — only fills slots if nothing else remains)
 *
 * Lower is better.
 */
export function seriesRegionRank(meta: SeriesMetadata): number {
  if (isHeadSeries(meta)) return 2;
  if (isBodySeries(meta)) return 0;
  return 1;
}

/**
 * Derive an axial/coronal/sagittal label when `meta.plane` is missing.
 * Tries ImageOrientationPatient (DICOM 0020,0037) first — the cross product
 * of the row/column direction cosines gives the slice normal. Falls back to
 * scanning the series description for plane keywords. Returns null if
 * neither signal is available.
 */
export function derivePlane(meta: SeriesMetadata): Plane | null {
  if (meta.plane === "Axial" || meta.plane === "Coronal" || meta.plane === "Sagittal") {
    return meta.plane;
  }
  return derivePlaneFromIop(meta.image_orientation_patient ?? null, meta.series_description);
}

/**
 * Lower-level variant for callers that already have IOP/description in hand
 * but don't have a full SeriesMetadata (e.g. `LabelingPage`'s memoized
 * computation over a normalized shape).
 */
export function derivePlaneFromIop(
  iop: number[] | null | undefined,
  seriesDescription?: string | null,
): Plane | null {
  if (iop && iop.length >= 6) {
    const nx = iop[1] * iop[5] - iop[2] * iop[4];
    const ny = iop[2] * iop[3] - iop[0] * iop[5];
    const nz = iop[0] * iop[4] - iop[1] * iop[3];
    const ax = Math.abs(nx);
    const ay = Math.abs(ny);
    const az = Math.abs(nz);
    if (az >= ax && az >= ay) return "Axial";
    if (ay >= ax) return "Coronal";
    return "Sagittal";
  }
  const desc = seriesDescription?.toLowerCase() ?? "";
  if (/\bsagittal\b|\bsag\b/.test(desc)) return "Sagittal";
  if (/\bcoronal\b|\bcor\b/.test(desc)) return "Coronal";
  if (/\baxial\b|\bax\b/.test(desc)) return "Axial";
  return null;
}

/**
 * Why this series cannot be reformatted (MPR'd) into another plane, or null
 * when it can. Single source of truth for the series right-click menu's
 * enablement AND the reason text it shows when disabled — the reason is the
 * thing the old "{plane} reconstruction unavailable" pane placeholder used to
 * carry, moved to where the user acts on it.
 *
 * `is_reconstructable === false` is tested explicitly (not falsy) because the
 * backend may omit the field entirely on older metadata, and an absent verdict
 * is not a negative one.
 */
export function reformatBlockerFor(meta: SeriesMetadata): string | null {
  if (meta.reconstruction_blocker) return meta.reconstruction_blocker;
  if (meta.is_reconstructable === false) return "not reconstructable";
  if ((meta.num_instances ?? 0) < 2) return "single_instance";
  return null;
}
